import React, { useEffect } from "react";
import { View, StyleSheet, Pressable, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { QRCode } from "@/components/QRCode";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import * as Linking from "expo-linking";

const PRINT_STYLES = `
@media print {
  body > * { visibility: hidden !important; }
  #qr-print-container, #qr-print-container * { visibility: visible !important; }
  #qr-print-container {
    position: fixed !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    background: white !important;
    box-shadow: none !important;
  }
  #qr-print-buttons { display: none !important; }
}
`;

export interface QRCodeModalProps {
  shiftId: string;
  onClose: () => void;
  onOpenKiosk?: () => void;
}

export function QRCodeModal({ shiftId, onClose, onOpenKiosk }: QRCodeModalProps) {
  const { colors } = useTheme();
  const clockInUrl = Linking.createURL(`/clock-in/${shiftId}`);

  // Inject print styles on web
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const styleId = "qr-print-styles";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = PRINT_STYLES;
    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  const handlePrint = () => {
    if (Platform.OS === "web") {
      window.print();
    }
  };

  return (
    <View nativeID="qr-print-container" style={[styles.container, { backgroundColor: colors.card }]}>
      {/* Title */}
      <ThemedText type="subtitle" style={styles.title}>
        Clock-In QR Code
      </ThemedText>
      <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
        Scan with phone camera to clock in
      </ThemedText>

      {/* QR Code */}
      <View style={[styles.qrWrapper, { backgroundColor: "#ffffff" }]}>
        <QRCode value={clockInUrl} size={200} color="#000000" backgroundColor="#ffffff" ecl="H" />
      </View>

      {/* Instructions */}
      <View style={styles.instructions}>
        <View style={styles.instructionRow}>
          <View style={[styles.stepBadge, { backgroundColor: colors.primary }]}>
            <ThemedText style={styles.stepNumber}>1</ThemedText>
          </View>
          <ThemedText style={[styles.instructionText, { color: colors.text }]}>Open phone camera</ThemedText>
        </View>
        <View style={styles.instructionRow}>
          <View style={[styles.stepBadge, { backgroundColor: colors.primary }]}>
            <ThemedText style={styles.stepNumber}>2</ThemedText>
          </View>
          <ThemedText style={[styles.instructionText, { color: colors.text }]}>Point at QR code</ThemedText>
        </View>
        <View style={styles.instructionRow}>
          <View style={[styles.stepBadge, { backgroundColor: colors.primary }]}>
            <ThemedText style={styles.stepNumber}>3</ThemedText>
          </View>
          <ThemedText style={[styles.instructionText, { color: colors.text }]}>Tap notification to check in</ThemedText>
        </View>
      </View>

      {/* Kiosk Mode Button (only shown when handler provided) */}
      {onOpenKiosk && (
        <>
          <Pressable
            onPress={onOpenKiosk}
            style={({ pressed, hovered }) => [
              styles.kioskButton,
              { backgroundColor: colors.backgroundSecondary, borderColor: colors.border },
              hovered && { opacity: 0.8 },
              pressed && { opacity: 0.7 },
            ]}
          >
            <MaterialCommunityIcons name="television" size={20} color={colors.text} />
            <ThemedText style={{ color: colors.text }}>Open in Kiosk Mode</ThemedText>
          </Pressable>

          {/* Warning */}
          <View
            style={[
              styles.warningContainer,
              { backgroundColor: colors.amber?.background, borderColor: colors.amber?.border },
            ]}
          >
            <MaterialCommunityIcons name="alert-outline" size={16} color={colors.amber?.text} />
            <ThemedText style={[styles.warningText, { color: colors.amber?.text }]}>
              Kiosk mode will sign you out of the app
            </ThemedText>
          </View>
        </>
      )}

      {/* Buttons */}
      <View nativeID="qr-print-buttons" style={styles.buttonRow}>
        {Platform.OS === "web" && (
          <Button
            icon="printer"
            text="Print"
            onPress={handlePrint}
            style={[styles.actionButton, { backgroundColor: colors.primary }]}
            textStyle={{ color: "white" }}
            iconColor="white"
          />
        )}
        <Button
          text="Close"
          onPress={onClose}
          style={[styles.actionButton, { backgroundColor: "transparent", borderColor: colors.border }]}
          textStyle={{ color: colors.text }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    maxWidth: 360,
    minWidth: 300,
    borderRadius: 16,
    padding: 24,
    alignItems: "center",
    gap: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: "600",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 14,
    textAlign: "center",
    marginTop: -8,
  },
  qrWrapper: {
    padding: 16,
    borderRadius: 12,
  },
  instructions: {
    width: "100%",
    gap: 10,
  },
  instructionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  stepBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  stepNumber: {
    color: "#ffffff",
    fontSize: 12,
    fontWeight: "bold",
  },
  instructionText: {
    fontSize: 14,
    flex: 1,
  },
  kioskButton: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    cursor: "pointer",
  },
  warningContainer: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  warningText: {
    fontSize: 12,
    flex: 1,
  },
  buttonRow: {
    width: "100%",
    flexDirection: "row",
    gap: 8,
  },
  actionButton: {
    flex: 1,
    minHeight: 44,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 8,
    borderWidth: 1,
  },
});
